import { filterBy, orderBy } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import CheckBoxCell from 'pages/tables/components/CheckBoxCell';
import PtrCell from 'pages/tables/components/ptrCell';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import 'scss/_tablePage.scss';
import * as list from '../../commons/models/list';
import Toolbar from './components/Toolbar';
import HeaderGridPrintTemplate from './HeaderGridPrintTemplate';
import DateFormatCell from './components/dateFormatCell';
import ButtonsCells from './components/DocumentButtonsCells';
import { Button } from '@progress/kendo-react-buttons';
import PlanificationButtonsCells from './components/PlanificationButtonsCells';
import CreatePlanification from 'pages/gant/components/Infos/CreatePlanification';

export default function GenericGrid(props) {
  var gridPDFExport;
  const [data, setData] = useState(list.stringify(props.data));
  const [, setExporting] = useState(false);
  const [filteredData, setFilteredData] = useState(list.stringify(data));
  const [sort, setSort] = useState(list.sort(props.definition));
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(props.take !== undefined ? props.take : 10);
  const [showAddModal, setShowAddModal] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setData(list.stringify(props.data));
  }, [props.data, setData]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const columnNames = useMemo(
    () => list.getColumnNames(props.definition),
    [props.definition]
  );

  const setRowEdit = (dataItem, inEdit) => {
    if (inEdit) {
      if (data.filter(d => d.inEdit == true).length === 0) {
        let newData = data.map(item => item.rid === dataItem.rid ? {
          ...item,
          inEdit: true
        } : item);
        setData(newData);
      }
    } else {
      let copiedData = JSON.parse(JSON.stringify(data));
      let newData = copiedData.find(item => item.rid === dataItem.rid);
      newData.inEdit = false;
      setData(copiedData);
    }
  };


  const isUpdatable = (defaultProps) => columnNames.find(c => c.field === defaultProps.field)?.updatable;
  const customCheckboxCell = defaultProps => <CheckBoxCell {...defaultProps} enableCheckbox={props.enableCheckbox} updatable={isUpdatable(defaultProps)} />;
  const customDateFormatCell = defaultProps => <DateFormatCell {...defaultProps} updatable={isUpdatable(defaultProps)} />;
  const customDocumentsButtonsCells = (defaultProps) => <ButtonsCells dataItem={defaultProps.dataItem} removeFile={(ridFile) => props.removeFile(ridFile)} visualizeFile={props.visualizeFile} onCreateClick={props.onCreateClick} documentUrlParameters={props.documentsUrlParameters} />;
  const customPlanificationButtonsCell = defaultProps => <PlanificationButtonsCells {...defaultProps} setRowEdit={setRowEdit} />;
  const gridColumns = useCallback(
    (columnNames) => {
      var columns = columnNames.map((item, i) => {
        if (item.field === 'prod_num' && item.label === 'PTR') {
          return (
            <GridColumn
              {...list.calculateWidth(
                item,
                i === columnNames.length - 1 && !props.showDocumentsButtonsCell && !props.planificationGrid,
                data,
                props.autoSize
              )}
              {...{ cell: PtrCell }}
              field={item.field}
              title={item.label}
              key={i}
            >
              {item.children.length !== 0
                ? gridColumns(item.children)
                : undefined}
            </GridColumn>
          );
        }

        if (
          item.field.includes('dt_') ||
          item.field.includes('date') ||
          item.field.includes('_dt') ||
          item.field === 'dtDeb'
        ) {
          return (
            <GridColumn
              {...list.calculateWidth(
                item,
                i === columnNames.length - 1 && !props.showDocumentsButtonsCell && !props.planificationGrid,
                data,
                props.autoSize,
                true
              )}
              {...{ cell: customDateFormatCell }}
              field={item.field}
              title={item.label}
              key={i}
              editable={true}
              editor='date'
            >
              {item.children.length !== 0
                ? gridColumns(item.children)
                : undefined}
            </GridColumn>
          );
        }


        return (
          <GridColumn
            {...list.calculateWidth(
              item,
              i === columnNames.length - 1 && !props.showDocumentsButtonsCell && !props.planificationGrid,
              data,
              props.autoSize
            )}
            {...(list.isCheckBox(data, item.field) ? { cell: customCheckboxCell } : {})}
            field={item.field}
            title={item.label}
            editable={isUpdatable({ field: item.field })}
            key={i}
          >
            {item.children.length !== 0
              ? gridColumns(item.children)
              : undefined}
          </GridColumn>
        );
      });
      if (props.showDocumentsButtonsCell) {
        columns.push((<GridColumn key={columns.length + 1} cell={customDocumentsButtonsCells} />));
      }

      if (props.planificationGrid) {
        columns.push((<GridColumn key={columns.length + 2} cell={customPlanificationButtonsCell} width={'500px'} title='Actions' />));
      }

      return columns;
    },
    [data, props.autoSize]
  );

  const exportPdf = () => {
    gridPDFExport.save(data, () => setExporting(false));
    setExporting(true);
  };

  const dataGrid = orderBy(filteredData, sort)
    .slice(skip, take + skip);

  const grid = (
    <Grid
      onItemChange={(e) => {
        const dataUpdate = data.map(item =>
          item.rid === e.dataItem.rid
            ? { ...item, [e.field]: e.value }
            : item
        );
        setData(dataUpdate);
        if (props.onItemChange) props.onItemChange(e);
      }}
      onRowClick={(e) => {
        if (props.rowClicked) {
          props.rowClicked(e.dataItem);
        }
      }}
      onRowDoubleClick={(e) => {
        if (props.rowDoubleClicked) {
          props.rowDoubleClicked(e.dataItem);
        }
      }}
      data={dataGrid}
      editField='inEdit'
      total={filteredData.length}
      skip={skip}
      take={take}
      pageable={{
        buttonCount: 10,
        pageSizes: [10, 20, 50, 100, 500],
      }}
      onPageChange={(event) => {
        setSkip(event.page.skip);
        setTake(event.page.take);
      }}
      sortable
      resizable
      sort={sort}
      onSortChange={(e) => setSort(e.sort)}
    >
      {props.planificationGrid && <GridToolbar>
        <Button title="Ajouter un requis" className="blueButton" onClick={() => { setShowAddModal(true); }}>
          Ajouter un requis
        </Button>
      </GridToolbar>}
      {gridColumns(columnNames)}
      <GridNoRecords>
        <br></br>
      </GridNoRecords>
    </Grid>
  );

  const toolbar = (
    <Toolbar
      canExport={props.canExport !== undefined ? props.canExport : false}
      exportPDF={exportPdf}
      showCreateBtn={props.showCreateBtn}
      txtBtn={props.txtBtn}
      onCreateClick={props.onCreateClick}
      showCreateBtn2={props.showCreateBtn2}
      txtBtn2={props.txtBtn2}
      onCreateClick2={props.onCreateClick2}
      showCheckbtn={props.checkBox}
      txtCheckBox={props.txtCheckBox}
      onCheckBoxClick={props.onCheckBoxClick}
      onSearchChange={(value) => {
        var filters = list.flatten(props.definition).map((item) => {
          return {
            field: item.key,
            operator: 'contains',
            value: value.target.value,
            ignoreCase: true,
          };
        });
        setFilteredData(
          filterBy(data, {
            logic: 'or',
            filters: filters,
          })
        );
      }}
    ></Toolbar>
  );

  const print = (
    <GridPDFExport
      margin='1cm'
      paperSize='A4'
      landscape={true}
      pageTemplate={() => (
        <HeaderGridPrintTemplate location={location.pathname} />
      )}
      ref={(pdfExport) => (gridPDFExport = pdfExport)}
    >
      {grid}
    </GridPDFExport>
  );

  return (
    <div>
      {props.hideToolbar ? null : toolbar}
      {grid}
      {showAddModal && (
        <CreatePlanification {...props} setShowAddModal={setShowAddModal} addRequis={true} />)}
      {print}
    </div>
  );
}

GenericGrid.propTypes = {
  onItemChange: PropTypes.func,
  autoSize: PropTypes.any,
  take: PropTypes.any,
  rowClicked: PropTypes.func,
  rowDoubleClicked: PropTypes.func,
  data: PropTypes.array,
  prod: PropTypes.bool,
  definition: PropTypes.array,
  onCreateClick: PropTypes.any,
  onCreateClick2: PropTypes.any,
  onCheckBoxClick: PropTypes.any,
  showCreateBtn: PropTypes.bool,
  showCreateBtn2: PropTypes.bool,
  checkBox: PropTypes.bool,
  txtBtn: PropTypes.string,
  txtBtn2: PropTypes.string,
  txtCheckBox: PropTypes.string,
  selectedRowId: PropTypes.string,
  canExport: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  enableCheckbox: PropTypes.string,
  showDocumentsButtonsCell: PropTypes.bool,
  removeFile: PropTypes.func,
  visualizeFile: PropTypes.func,
  documentsUrlParameters: PropTypes.any,
  planificationGrid: PropTypes.bool,
  media: PropTypes.any,
  camp_seq: PropTypes.any,
  project: PropTypes.any
};
